import React, { useState, useEffect, useRef } from 'react';
import { 
  Modal, 
  Box, 
  IconButton, 
  Typography, 
  CircularProgress,
  Alert,
  Button
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DownloadIcon from '@mui/icons-material/Download';
import ReactMarkdown from 'react-markdown';
import { useTutorial } from '../contexts/TutorialContext';
import { useCustomAuth } from '../contexts/AuthContext';
import { authFetch } from '../utils/authFetch';

const ContentViewingModal = () => {
  const { 
    selectedContent, 
    contentUrl, 
    clearSelectedContent, 
    updateContentProgress,
    userProgress 
  } = useTutorial();
  
  const { isAdminMode } = useCustomAuth();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [textContent, setTextContent] = useState('');
  const [loadingText, setLoadingText] = useState(false);
  
  // Auto-tracking states
  const [autoCompleteStatus, setAutoCompleteStatus] = useState('');
  const [viewingProgress, setViewingProgress] = useState(0);
  const textScrollRef = useRef(null);
  const videoRef = useRef(null);

  const isOpen = !!selectedContent;
  
  // Get content status
  const contentStatus = selectedContent && userProgress 
    ? userProgress.getContentStatus(selectedContent.itemId)
    : { required: false, completed: false };

  const handleClose = () => {
    clearSelectedContent();
    setError(null);
  };

  const handleMarkComplete = async () => {
    if (!selectedContent || isAdminMode) return;
    
    try {
      setLoading(true);
      await updateContentProgress(selectedContent.itemId, true);
    } catch (err) {
      setError('Failed to mark content as complete');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkIncomplete = async () => {
    if (!selectedContent || isAdminMode) return;
    
    try {
      setLoading(true);
      await updateContentProgress(selectedContent.itemId, false);
    } catch (err) {
      setError('Failed to mark content as incomplete');
    } finally {
      setLoading(false);
    }
  };

  // Auto-complete function
  const handleAutoComplete = async (reason) => {
    if (!selectedContent || contentStatus.completed || isAdminMode) return;
    
    try {
      setAutoCompleteStatus(`Auto-completed: ${reason}`);
      await updateContentProgress(selectedContent.itemId, true);
      
      // Show success message for a few seconds
      setTimeout(() => {
        setAutoCompleteStatus('');
      }, 3000);
    } catch (err) {
      setError('Failed to auto-complete content');
    }
  };

  // Video completion tracking
  const handleVideoTimeUpdate = () => {
    if (!videoRef.current || isAdminMode) return;
    
    const video = videoRef.current;
    const progress = (video.currentTime / video.duration) * 100;
    setViewingProgress(progress);
    
    // Auto-complete when video reaches 95% or ends
    if (progress >= 95 && !contentStatus.completed) {
      handleAutoComplete('Video watched to 95%');
    }
  };

  const handleVideoEnded = () => {
    if (!contentStatus.completed && !isAdminMode) {
      handleAutoComplete('Video completed');
    }
  };

  // PDF load and scroll handlers (kept for compatibility)
  const handlePDFLoad = () => {
    // PDF load handler - no longer used for timing
  };

  const handlePDFScroll = () => {
    // PDF scroll handler - can be used for other purposes if needed
  };

  // Text scrolling tracking
  const handleTextScroll = () => {
    if (!textScrollRef.current || isAdminMode) return;
    
    const container = textScrollRef.current;
    const scrollTop = container.scrollTop;
    const scrollHeight = container.scrollHeight;
    const clientHeight = container.clientHeight;
    
    // Check if content is short (no scroll needed)
    if (scrollHeight <= clientHeight + 5) { // +5 for small margin
      // Content fits in one view, auto-complete immediately
      if (!contentStatus.completed) {
        handleAutoComplete('Short document fully visible');
      }
      return;
    }
    
    const scrollProgress = (scrollTop / (scrollHeight - clientHeight)) * 100;
    setViewingProgress(scrollProgress);
    
    // Auto-complete when scrolled to 90% of the document
    if (scrollProgress >= 90 && !contentStatus.completed) {
      handleAutoComplete('Document read to 90%');
    }
  };

  // Load text content when modal opens for text files
  useEffect(() => {
    const loadTextContent = async () => {
      if (!selectedContent || selectedContent.contentType !== 'text' || !contentUrl) {
        setTextContent('');
        return;
      }

      setLoadingText(true);
      setError(null);
      
      try {
        // Use regular fetch for S3 presigned URLs, not authFetch
        const response = await fetch(contentUrl);
        if (!response.ok) {
          throw new Error('Failed to load text content');
        }
        const text = await response.text();
        setTextContent(text);
      } catch (err) {
        console.error('Error loading text content:', err);
        setError('Failed to load text content. Please try downloading the file.');
      } finally {
        setLoadingText(false);
      }
    };

    if (isOpen && selectedContent?.contentType === 'text') {
      loadTextContent();
    }
  }, [selectedContent, contentUrl, isOpen]);

  // Check content size after text content is loaded and rendered
  useEffect(() => {
    if (textContent && selectedContent?.contentType === 'text' && !loadingText && !isAdminMode) {
      // Small delay to ensure content is rendered in DOM
      const timer = setTimeout(() => {
        handleTextScroll();
      }, 200);
      
      return () => clearTimeout(timer);
    }
  }, [textContent, selectedContent?.contentType, loadingText, contentStatus.completed, isAdminMode]);

  // Clear text content and reset auto-tracking when modal closes
  useEffect(() => {
    if (!isOpen) {
      setTextContent('');
      setLoadingText(false);
      setAutoCompleteStatus('');
      setViewingProgress(0);
    }
  }, [isOpen]);

  // Reset auto-tracking states when content changes
  useEffect(() => {
    setAutoCompleteStatus('');
    setViewingProgress(0);
  }, [selectedContent?.itemId]);

  // Render content based on type
  const renderContent = () => {
    if (!selectedContent || !contentUrl) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
          <CircularProgress />
        </Box>
      );
    }

    switch (selectedContent.contentType) {
      case 'video':
        return (
          <Box
            sx={{
              width: '100%',
              height: '0',
              paddingBottom: '56.25%', // 16:9 aspect ratio
              position: 'relative',
            }}
          >
            <Box
              component="video"
              controls
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
              }}
              src={contentUrl}
              onError={() => setError('Failed to load video')}
              onTimeUpdate={handleVideoTimeUpdate}
              onEnded={handleVideoEnded}
              ref={videoRef}
            />
            {/* Progress indicator */}
            {!isAdminMode && viewingProgress > 0 && viewingProgress < 100 && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  background: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  fontSize: '0.75rem'
                }}
                              >
                  Progress: {Math.round(viewingProgress)}%
                </Box>
            )}
          </Box>
        );

            case 'pdf':
        return (
          <Box sx={{ width: '100%', height: '600px', display: 'flex', flexDirection: 'column' }}>
            {/* PDF Display Area - with prominent Open button */}
            <Box sx={{ 
              flex: 1, 
              position: 'relative', 
              backgroundColor: '#f5f5f5',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column'
            }}>
              {/* PDF Icon and Open Button */}
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                p: 4 
              }}>
                <Box sx={{ 
                  width: '80px', 
                  height: '80px', 
                  backgroundColor: '#fff',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 3,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }}>
                  <Typography sx={{ 
                    fontSize: '24px', 
                    fontWeight: 'bold', 
                    color: '#d32f2f' 
                  }}>
                    PDF
                  </Typography>
                </Box>
                
                <Typography variant="h6" sx={{ mb: 1, color: '#333' }}>
                  {selectedContent.title}
                </Typography>
                
                <Button
                  variant="contained"
                  size="large"
                  onClick={() => {
                    window.open(contentUrl, '_blank');
                    // Auto-complete when PDF is opened
                    if (!contentStatus.completed && !isAdminMode) {
                      handleAutoComplete('PDF opened');
                    }
                  }}
                  sx={{ 
                    textTransform: 'none',
                    fontSize: '16px',
                    px: 4,
                    py: 1.5
                  }}
                >
                  Open
                </Button>
              </Box>
              
              {/* Small iframe for thumbnail if needed */}
              <iframe
                src={contentUrl}
                width="1"
                height="1"
                style={{ 
                  border: 'none', 
                  position: 'absolute', 
                  top: '-100px', 
                  opacity: 0 
                }}
                title={selectedContent.title}
                onLoad={handlePDFLoad}
              />
            </Box>
            
            {/* Download section */}
            <Box sx={{ p: 2, backgroundColor: '#f9f9f9', borderTop: '1px solid #e0e0e0' }}>
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                <Button
                  variant="outlined"
                  size="small"
                  component="a"
                  href={contentUrl}
                  download
                  onClick={() => {
                    // Auto-complete when PDF is downloaded
                    if (!contentStatus.completed && !isAdminMode) {
                      handleAutoComplete('PDF downloaded');
                    }
                  }}
                  sx={{ textTransform: 'none' }}
                >
                  Download PDF
                </Button>
              </Box>
            </Box>
          </Box>
        );

      case 'text':
        const isMarkdown = selectedContent?.s3Key?.toLowerCase().endsWith('.md') || 
                          selectedContent?.s3Key?.toLowerCase().endsWith('.markdown');
        
        return (
          <Box sx={{ width: '100%', height: '600px', display: 'flex', flexDirection: 'column' }}>
            {/* Text Display Area */}
            <Box 
              ref={textScrollRef}
              onScroll={handleTextScroll}
              sx={{ 
                flex: 1, 
                p: 3, 
                overflow: 'auto', 
                backgroundColor: '#fafafa',
                position: 'relative'
              }}
            >
              {loadingText ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                  <CircularProgress />
                </Box>
              ) : isMarkdown ? (
                <Box
                  sx={{
                    '& h1': { fontSize: '2rem', fontWeight: 700, mb: 2, color: '#333' },
                    '& h2': { fontSize: '1.5rem', fontWeight: 600, mb: 1.5, color: '#333' },
                    '& h3': { fontSize: '1.25rem', fontWeight: 600, mb: 1, color: '#333' },
                    '& p': { mb: 1.5, lineHeight: 1.6, color: '#555' },
                    '& ul, & ol': { mb: 1.5, pl: 2 },
                    '& li': { mb: 0.5, lineHeight: 1.6 },
                    '& code': { 
                      backgroundColor: '#f0f0f0', 
                      padding: '2px 4px', 
                      borderRadius: '3px',
                      fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                      fontSize: '0.875rem'
                    },
                    '& pre': { 
                      backgroundColor: '#f8f8f8', 
                      padding: 2, 
                      borderRadius: 1,
                      overflow: 'auto',
                      fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                      fontSize: '0.875rem'
                    },
                    '& blockquote': {
                      borderLeft: '4px solid #ddd',
                      pl: 2,
                      ml: 0,
                      fontStyle: 'italic',
                      color: '#666'
                    },
                    '& strong': { fontWeight: 600 },
                    '& em': { fontStyle: 'italic' }
                  }}
                >
                  <ReactMarkdown>{textContent}</ReactMarkdown>
                </Box>
              ) : (
                <Typography
                  component="pre"
                  sx={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                    fontSize: '0.875rem',
                    lineHeight: 1.6,
                    margin: 0,
                    color: '#333',
                  }}
                >
                  {textContent}
                </Typography>
              )}
              
              {/* Text reading progress indicator */}
              {!isAdminMode && viewingProgress > 0 && viewingProgress < 100 && selectedContent.contentType === 'text' && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    background: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    fontSize: '0.75rem'
                  }}
                >
                  Reading progress: {Math.round(viewingProgress)}%
                </Box>
              )}
            </Box>
          </Box>
        );

      default:
        return (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              Unsupported content type: {selectedContent.contentType}
            </Typography>
          </Box>
        );
    }
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="content-modal-title"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Box
        sx={{
          position: 'relative',
          width: '90%',
          maxWidth: '1000px',
          maxHeight: '90vh',
          bgcolor: '#fff',
          borderRadius: 2,
          boxShadow: 24,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderBottom: '1px solid #e0e0e0',
            backgroundColor: '#f5f5f5',
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography 
              id="content-modal-title" 
              variant="h6" 
              component="h2"
              sx={{ fontWeight: 600 }}
            >
              {selectedContent?.title}
            </Typography>
            {selectedContent?.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {selectedContent.description}
              </Typography>
            )}
          </Box>
          
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              color: '#666',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Error display */}
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'hidden' }}>
          {renderContent()}
        </Box>

        {/* Footer with completion controls */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderTop: '1px solid #e0e0e0',
            backgroundColor: '#f9f9f9',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Content Type: {selectedContent?.contentType?.toUpperCase()}
            </Typography>
            {!isAdminMode && contentStatus.required && (
              <Typography variant="body2" color="error">
                • Required
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Download button for PDF and Text */}
            {(selectedContent?.contentType === 'pdf' || selectedContent?.contentType === 'text') && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                component="a"
                href={contentUrl}
                download
                onClick={() => {
                  // Auto-complete when file is downloaded
                  if (!contentStatus.completed && !isAdminMode) {
                    const fileType = selectedContent.contentType === 'pdf' ? 'PDF' : 'File';
                    handleAutoComplete(`${fileType} downloaded`);
                  }
                }}
              >
                Download
              </Button>
            )}
            
            {/* Auto-complete status */}
            {!isAdminMode && autoCompleteStatus && (
              <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
                {autoCompleteStatus}
              </Typography>
            )}
            
            {!isAdminMode && (contentStatus.completed ? (
              <>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon sx={{ color: '#388e3c' }} />
                  <Typography variant="body2" color="success.main">
                    Completed
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleMarkIncomplete}
                  disabled={loading}
                >
                  Mark Incomplete
                </Button>
              </>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                  {selectedContent?.contentType === 'video' && 'Auto-complete at 95% watched'}
                  {selectedContent?.contentType === 'pdf' && 'Auto-complete when opening PDF'}
                  {selectedContent?.contentType === 'text' && 'Auto-complete at 90% read (or when fully visible)'}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleMarkComplete}
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={16} /> : <CheckCircleIcon />}
                >
                  Manual Complete
                </Button>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default ContentViewingModal; 